// @flow

import {
    api_id,
    apply,
    type Mapper,
    shallowCopy,
    setLocale,
    localizeAddress,
} from "../shared";
import slugify from "slugify";

import { createProjectAssetPath } from "../../endpoints";
import { GATSBY_C21_IMAGES_BASEPATH } from "../../env";
import ImageMapper from "../image";

import { gettext, addLocale, useLocale } from "ttag";
import { numberToCurrency } from "../../../currency";

const properties___NODE =
    ({ createC21PropertyId }: *) =>
    ({ properties, locale }: *) => {
        // eslint-disable-next-line no-unused-vars
        const result = properties.map((property) => {
            const { id } = property;
            return createC21PropertyId({ id, locale });
        });

        // FIX: return empty array because properties IDs don't exist in properties api
        return { properties___NODE: [] };
    };

const cleanupAfterSourceMapped = (project: *) => {
    delete project.images;

    delete project._embedded;
    if (project.agent) delete project.agent.images;

    return project;
};

const cleanupAfterSearchMapped = (project: *) => {
    delete project._embedded;
    delete project._links;

    if (project.agent) delete project.agent.images;

    return project;
};

const cleanup = (project: *) => {
    delete project.properties;
    delete project._links;
};

const slug = ({ locale, address: { city }, api_id }) => {
    const slug = ["", locale, "project", slugify(city)].join("/").toLowerCase();

    return {
        slug: `${slug}/${api_id}`,
    };
};

const assets = ({ id, assets }: *) => ({
    assets: assets.map((asset: *) => ({
        ...asset,
        href: `${GATSBY_C21_IMAGES_BASEPATH}/${createProjectAssetPath({
            id,
        })}/${asset.name}`,
    })),
});

const images = (project: *) => {
    const path = createProjectAssetPath(project);

    return {
        images: project.images
            .map(({ name, description, lastModifiedDate }: *) => {
                const [, filename, extension] = name.match(/(.+)(\..+)/);

                return {
                    description,
                    path,
                    filename,
                    extension,
                    lastModifiedDate,
                };
            })
            .map((image) => {
                return {
                    description: image.description,
                    gatsbyImageData: (size) =>
                        ImageMapper.toGatsbyImageData(image, size),
                    lastModifiedDate: image.lastModifiedDate,
                };
            }),
    };
};

const agent =
    ({ createC21ImageId }: *) =>
    (project: *) => {
        const agent = project._embedded.agent
            ? { ...project._embedded.agent }
            : null;

        if (!agent) return { agent: null };

        agent.images___NODE = agent.images.map((image) =>
            createC21ImageId(createProjectAssetPath(project), image.name),
        );

        return { agent };
    };

const agency =
    ({ createC21AgencyId }: *) =>
    (project: *) => ({
        agency___NODE: createC21AgencyId({
            id: project._links.agency.id,
            locale: project.locale,
        }),
    });

const images___NODE =
    ({ createC21ImageId }: *) =>
    (project: *) => ({
        images___NODE: project.images.map((image) =>
            createC21ImageId(createProjectAssetPath(project), image.name),
        ),
    });

const localizeTitle = ({ title }: *, locale: *) => ({
    title: title[locale],
});

const localizeDescription = ({ description }: *, locale: *) => ({
    description: description[locale],
});

const localizePrice = ({ price }: *) => ({
    price: price.hidden
        ? gettext("common.price.on-demand")
        : numberToCurrency(price.amount),
});

const createSourcePluginMapper = (gatsbyApi: *): Mapper => ({
    map: (project: *) => {
        const steps: *[] = [
            api_id,
            shallowCopy,
            assets,
            agent(gatsbyApi),
            agency(gatsbyApi),
            assets,
            images___NODE(gatsbyApi),
        ];

        const mapped = apply(steps, (step) => step(project));

        cleanupAfterSourceMapped(mapped);

        return mapped;
    },
    localize: (project: *, locale: string) => {
        const steps: *[] = [
            shallowCopy,
            setLocale,
            localizeTitle,
            localizeDescription,
            localizeAddress,
        ];

        return apply(steps, (step) => step(project, locale));
    },
    mapLocalized: (project: *, translations: *) => {
        addLocale(project.locale, translations);
        useLocale(project.locale);

        const steps: *[] = [
            shallowCopy,
            slug,
            properties___NODE(gatsbyApi),
            agency(gatsbyApi),
            localizePrice,
        ];

        const mapped = apply(steps, (step) => step(project));

        cleanup(mapped);

        return mapped;
    },
});

const createSearchMapper = (): Mapper => ({
    map: (project: *) => {
        const steps: *[] = [api_id, shallowCopy, images];

        const mapped = apply(steps, (step) => step(project));

        cleanupAfterSearchMapped(mapped);

        return mapped;
    },
    localize: (project: *, locale: string) => {
        const steps: *[] = [
            shallowCopy,
            setLocale,
            localizeTitle,
            localizeAddress,
            localizePrice,
        ];

        return apply(steps, (step) => step(project, locale));
    },
    mapLocalized: (project: *) => {
        const steps: *[] = [shallowCopy, slug];

        return apply(steps, (step) => step(project));
    },
});

const MapperFactory = {
    forSourcePlugin: (gatsbyApi: *): Mapper =>
        createSourcePluginMapper(gatsbyApi),
    forSearch: (): Mapper => createSearchMapper(),
};

export default MapperFactory;

export const mapProjectsForSearch = (data: *, locale: *) => {
    const projectMapper = MapperFactory.forSearch();

    return data
        .map(projectMapper.map)
        .map((project) => projectMapper.localize(project, locale))
        .map(projectMapper.mapLocalized)
        .map((project) => ({
            ...project,
            images: project.images.map((image) => ({
                description: image.description,
                fixed: image.fixed({
                    width: 310,
                    height: 177,
                }),
                lastModifiedDate: image.lastModifiedDate,
            })),
        }));
};
